'use client';
import { useEffect, useState, useCallback } from 'react';
import { FiX } from 'react-icons/fi';
import { setContactQuota, refreshContactQuota } from '@/lib/hooks/useContactQuota';
import UnlockPaywall from '@/components/UnlockPaywall';

interface Member {
  appId: number;
  name: string;
  mobile: string;
  unlocked: boolean;
  gender: string;
  teacher_category: string;
  class: string;
  subject: string;
  current_school_name: string;
  district: string;
  block: string;
  unreachable?: boolean;
}
export interface Chain {
  members: Member[];
  you_move_to: { district: string; block: string; school: string };
  credits_required: number;
}
interface RevealedContact { appId: number; name: string; mobile: string }
interface QuotaInfo { used: number; limit: number; remaining: number; isExceeded: boolean }

/**
 * ChainMatches
 * - Default (results pages): auto-searches on mount and self-hides when the
 *   user has no chains / isn't logged in.
 * - `showcase` (the /bihar/mutual-transfer feature page): always renders a
 *   feature card explaining 3-way chains; the search only runs when the user
 *   clicks the button, and the result (including "none found" / "sign in")
 *   is shown inline instead of hiding.
 * - `chains` (the dashboard tab): the parent already fetched them to put a
 *   count on the tab, so this renders those instead of asking again.
 */
export default function ChainMatches({
  showcase = false,
  autoLoad = false,
  chains: chainsProp,
  loadingProp,
  onRevealed,
}: {
  showcase?: boolean;
  autoLoad?: boolean;
  chains?: Chain[];
  loadingProp?: boolean;
  onRevealed?: () => void;
}) {
  const supplied = chainsProp !== undefined;
  const [chains, setChains] = useState<Chain[]>(chainsProp ?? []);
  const [loading, setLoading] = useState(supplied ? !!loadingProp : !showcase); // showcase waits for a click
  const [started, setStarted] = useState(supplied ? true : !showcase); // has a search been run yet?
  const [error, setError] = useState<string | null>(null);
  const [revealing, setRevealing] = useState<number | null>(null);
  const [revealed, setRevealed] = useState<Record<number, RevealedContact>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [page, setPage] = useState(1); // 100 chains per page
  // Out of credits isn't an error — it's the paywall. Remember which chain they
  // were trying to open so we can finish the reveal right after they pay.
  const [paywall, setPaywall] = useState<{ idx: number; chain: Chain; quotaInfo: QuotaInfo | null } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setStarted(true);
    setError(null);
    try {
      const res = await fetch('/api/bihar/mutual-transfer/chain-matches', { credentials: 'include', cache: 'no-store' });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load');
      setChains(data.chains || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load chain matches');
    } finally {
      setLoading(false);
    }
  }, []);

  // When the parent owns the fetch, mirror it rather than asking again.
  const [prevChainsProp, setPrevChainsProp] = useState(chainsProp);
  if (supplied && chainsProp !== prevChainsProp) {
    setPrevChainsProp(chainsProp);
    setChains(chainsProp);
  }
  const [prevLoadingProp, setPrevLoadingProp] = useState(loadingProp);
  if (supplied && loadingProp !== prevLoadingProp) {
    setPrevLoadingProp(loadingProp);
    setLoading(!!loadingProp);
  }

  useEffect(() => {
    if (supplied) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial load flag; effect also performs async work
    if (!showcase || autoLoad) load();
  }, [supplied, showcase, autoLoad, load]);

  const reveal = useCallback(async (idx: number, chain: Chain) => {
    setRevealing(idx);
    setMsg(null);
    try {
      const members = chain.members.map((m) => m.appId);
      const res = await fetch('/api/bihar/mutual-transfer/chain-matches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ members }),
      });
      const data = await res.json();

      if (data.quotaInfo) {
        setContactQuota({
          used: data.quotaInfo.used,
          limit: data.quotaInfo.limit,
          remaining: data.quotaInfo.remaining,
        });
      }

      // 403 = the quota gate, not a failure. Show the same pay-here paywall the
      // single-contact button shows instead of a red "you have 0 credits" line.
      if (res.status === 403 && typeof data.credits_required === 'number') {
        // No quotaInfo means no subscription at all — say "locked", not "used up".
        setPaywall({
          idx,
          chain,
          quotaInfo: data.quotaInfo ?? { used: 0, limit: 0, remaining: 0, isExceeded: true },
        });
        return false;
      }

      if (!res.ok || !data.success) throw new Error(data.error || 'Could not reveal');
      setPaywall(null);
      const map = { ...revealed };
      for (const ct of data.contacts as RevealedContact[]) map[ct.appId] = ct;
      setRevealed(map);
      // `revealed` dies with this component, and when the parent owns the data
      // a tab switch remounts it — tell the parent so the unlocked contacts
      // come back from the server instead of re-masking.
      onRevealed?.();
      return true;
    } catch (e) {
      setMsg(e instanceof Error ? e.message : 'Could not reveal');
      return false;
    } finally {
      setRevealing(null);
    }
  }, [onRevealed, revealed]);

  // ---- The list of chain cards (shared by both modes) ----
  const PER_PAGE = 100;
  const totalPages = Math.max(1, Math.ceil(chains.length / PER_PAGE));
  const curPage = Math.min(page, totalPages);
  const renderChains = () => (
    <div className="space-y-4">
      {chains.slice((curPage - 1) * PER_PAGE, curPage * PER_PAGE).map((chain, i) => {
        const idx = (curPage - 1) * PER_PAGE + i; // original index (reveal()/revealing key off this)
        const anyLocked = chain.members.some((m) => !revealed[m.appId] && !m.unlocked);
        return (
          <div key={idx} className="rounded-xl border border-purple-100 bg-gradient-to-br from-purple-50/50 to-white p-4 shadow-sm">
            <div className="text-sm font-semibold text-purple-800 mb-3">
              You move to → {chain.you_move_to.block}, {chain.you_move_to.district}
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              {chain.members.map((m) => {
                const rc = revealed[m.appId];
                const name = rc?.name ?? m.name;
                const mobile = rc?.mobile ?? m.mobile;
                const isOpen = !!rc || m.unlocked;
                return (
                  <div key={m.appId} className="rounded-lg border border-gray-200 bg-white p-3">
                    <div className="font-semibold text-gray-900">{name}</div>
                    <div className="text-xs text-gray-500 mb-1">
                      {m.teacher_category} • {m.class} • {m.subject} • {m.gender}
                    </div>
                    <div className="text-sm text-gray-700">{m.current_school_name}</div>
                    <div className="text-xs text-gray-500">{m.block}, {m.district}</div>
                    {m.unreachable && (
                      <div className="text-xs font-medium text-amber-700">Unreachable</div>
                    )}
                    <div className={`mt-2 text-sm font-medium ${isOpen ? 'text-green-700' : 'text-gray-400'}`}>
                      📱 {mobile}
                      {isOpen && (
                        <a
                          href={`https://wa.me/91${String(mobile).replace(/\D/g, '').slice(-10)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="ml-2 text-green-600 underline"
                        >
                          WhatsApp
                        </a>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {anyLocked ? (
              <>
              {/* Only about the members still being paid for. Sits above the button so it
                  is read before the click — a chain reveal with credits in hand never
                  passes through the paywall, so this is the only place to say it. */}
              {chain.members.some((m) => m.unreachable && !revealed[m.appId] && !m.unlocked) && (
                <div className="mt-3 rounded-lg bg-amber-50 px-3 py-2 ring-1 ring-amber-300">
                  <p className="text-xs font-semibold leading-tight text-amber-900">
                    This chain includes a number that has never replied to us. Reveal at your own risk.
                  </p>
                  <p lang="hi" className="mt-0.5 text-xs leading-tight text-amber-800/80">
                    इस चेन में एक नंबर ने हमें कभी जवाब नहीं दिया — अपने जोखिम पर आगे बढ़ें।
                  </p>
                </div>
              )}
              <button
                onClick={() => reveal(idx, chain)}
                disabled={revealing === idx}
                className="mt-3 w-full sm:w-auto bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors"
              >
                {revealing === idx ? 'Revealing…' : `🔓 Reveal both contacts (${chain.credits_required} credit${chain.credits_required === 1 ? '' : 's'})`}
              </button>
              </>
            ) : (
              <div className="mt-3 text-sm text-green-700 font-medium">✅ Contacts unlocked</div>
            )}
          </div>
        );
      })}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3 pt-1">
          <button
            disabled={curPage <= 1}
            onClick={() => setPage(curPage - 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            ← Prev
          </button>
          <span className="text-xs text-gray-600">
            Page <span className="font-semibold">{curPage}</span> / {totalPages} · {chains.length} total
          </span>
          <button
            disabled={curPage >= totalPages}
            onClick={() => setPage(curPage + 1)}
            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );

  // ---- The paywall overlay (rendered in both modes) ----
  const PaywallModal = paywall ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#101B2D]/40 p-4 backdrop-blur-sm">
      <div className="relative w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-[0_24px_60px_-24px_rgba(16,27,45,0.6)] ring-1 ring-[#101B2D]/10">
        <button
          onClick={() => setPaywall(null)}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 rounded-full p-1.5 text-[#101B2D]/40 transition-colors hover:bg-[#101B2D]/5 hover:text-[#101B2D]"
        >
          <FiX size={16} />
        </button>
        <UnlockPaywall
          kind="contacts"
          quotaInfo={paywall.quotaInfo}
          unreachableWarning={paywall.chain.members.some((m) => m.unreachable && !m.unlocked)}
          onUnlocked={async () => {
            await refreshContactQuota();
            const ok = await reveal(paywall.idx, paywall.chain);
            if (ok) setPaywall(null);
          }}
          onDismiss={() => setPaywall(null)}
          unlockingLabel="Opening both contacts…"
        />
      </div>
    </div>
  ) : null;

  // ---- Feature-card header (shown in showcase mode always; on results pages only when chains exist) ----
  const Header = (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="text-xl">🔗</span>
        <h3 className="text-lg font-bold text-gray-900">3-Way Chain Matches</h3>
        {chains.length > 0 && (
          <span className="text-xs font-medium bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">{chains.length}</span>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        No direct swap? A 3-way rotation (A→B→C→A) can still move all three of you. Revealing a chain shows <b>both</b> other teachers&apos; contacts at once.
      </p>
    </div>
  );

  // ---- SHOWCASE MODE: feature card with a Search button ----
  if (showcase) {
    return (
      <div className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50/60 to-white p-5 sm:p-6 shadow-sm">
        {Header}

        {!started && !autoLoad && (
          <button
            onClick={load}
            className="inline-flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors"
          >
            <span>🔍</span> Search my 3-way chain matches
          </button>
        )}

        {loading && <div className="text-gray-500 text-sm py-2">Searching for 3-way rotations…</div>}

        {started && !loading && error && (
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-3">
            Please <b>sign in</b> and <b>fill your transfer form</b> first, then search again to find 3-way chains for your profile.
          </div>
        )}

        {started && !loading && !error && chains.length === 0 && (
          <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-3">
            No 3-way chains found for your profile right now. As more teachers join, rotations open up — check back later.
          </div>
        )}

        {started && !loading && !error && chains.length > 0 && (
          <>
            {msg && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{msg}</div>}
            {renderChains()}
          </>
        )}
        {PaywallModal}
      </div>
    );
  }

  // ---- RESULTS-PAGE MODE: auto-loaded, self-hides when empty ----
  if (loading) return <div className="text-gray-500 text-sm py-4">Loading chain matches…</div>;
  if (error) return null; // silently hide on error (feature is additive)
  if (chains.length === 0) return null;

  return (
    <div className="mt-8">
      {Header}

      {msg && <div className="mb-3 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{msg}</div>}

      {renderChains()}

      {PaywallModal}
    </div>
  );
}
